import { useCallback, useEffect, useRef, useState } from 'react';
import { useCopyToClipboard } from 'react-use';
import Button from "@material-ui/core/Button";
import Tooltip from "@material-ui/core/Tooltip";
import makeStyles from "@material-ui/core/styles/makeStyles";

import { useI18n } from '../hooks/i18n-hooks';


interface CopyLinkProps {
	tooltipOpen: boolean | number | undefined;
	id?: string | number;
	setTooltipOpen: React.Dispatch<React.SetStateAction<boolean | number | undefined>>;
	toCopy: string | number | Date;
}

const CopyLink: React.FC<CopyLinkProps> = ({ tooltipOpen, id, setTooltipOpen, toCopy }) => {

	const [keepTooltipOpen, setKeepTooltipOpen] = useState(false);

	const classes = useStyles();
	const [, copyToClipboard] = useCopyToClipboard();

	const copyText = useI18n(i => i.freeSale);
	const noLink = useI18n(i => i.errText.noLink);


	const timer = useRef<NodeJS.Timeout>();

	const copyLinkToClipboard = useCallback(() => {
		if (!toCopy) return alert(noLink);

		copyToClipboard(toCopy.toString());
		setTooltipOpen(typeof id === 'number' ? id : true);

		timer.current = setTimeout(() => {
			setTooltipOpen(undefined);
		}, 2000);

	}, [copyToClipboard, toCopy, id]);

	useEffect(() => () => timer.current && clearTimeout(timer.current), []);


	// Tooltip open will be either an id or a bool.  If its a bool + true => return true ||, is id && tooltip open === id passed -> return true (this is used when this component is used in a map and only one of the items should have the tooltip appear)
	useEffect(() => {
		setKeepTooltipOpen(typeof tooltipOpen === 'boolean' ? tooltipOpen : id === tooltipOpen)
	}, [tooltipOpen, id])

	useEffect(() => {

		if (!keepTooltipOpen) return;

		const timeoutId = setTimeout(() => {
			setKeepTooltipOpen(false);
		}, 2000);

		return () => {
			clearTimeout(timeoutId);
		}

	}, [keepTooltipOpen])

	return (
		<div>
			<Tooltip
				PopperProps={{
					disablePortal: true,
				}}
				open={keepTooltipOpen}
				disableFocusListener
				disableHoverListener
				disableTouchListener
				title={copyText.copied}
				classes={{
					tooltip: classes.tooltip,
					popper: classes.tooltip
				}}
			>
				<Button
					color="primary"
					onClick={copyLinkToClipboard}
					classes={{
						root: classes.button,
						label: classes.buttonLabel
					}}
					startIcon={<img src='/lulav-icons/content-copy.svg' className={classes.copyIcon} />}
				>
					{copyText.copy}
				</Button>
			</Tooltip>
		</div>
	);
}

export default CopyLink;

const useStyles = makeStyles(theme => ({
	tooltip: {
		width: 'fit-content',
		minWidth: 'unset !important'
	},

	buttonLabel: {
		fontSize: 16.5,
		textTransform: 'none',
		fontFamily: 'Assistant-SemiBold !important',
		[theme.breakpoints.up(1600)]: {
			fontSize: 20,
		}
	},

	button: {
		marginLeft: 15,
		paddingLeft: 11,
		paddingRight: 11,
		boxSizing: 'border-box',
		whiteSpace: 'nowrap',

		[theme.breakpoints.down(800)]: {
			border: `1px solid ${theme.palette.primary.main}6E`,
			borderRadius: 8,
			paddingTop: 4,
			paddingBottom: 4,
		},

		[theme.breakpoints.up(1600)]: {
			marginLeft: 20,
			paddingLeft: 15,
			paddingRight: 15,
		},

		[theme.breakpoints.down(1000)]: {
			marginLeft: 0,
		}
	},
	copyIcon: {
		transform: 'scale(0.9)'
	}
}));